import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { FormLabelUI } from '../Interface';

const OPERATORS = [
  { value: 'contains', label: 'tartalmazza' },
  { value: 'notContains', label: 'nem tartalmazza' },
  { value: 'startsWith', label: 'ezzel kezdődik' },
  { value: 'endsWith', label: 'ezzel végződik' },
  { value: 'equals', label: 'egyenlő' },
];

const defaultRow = () => ({ id: uuidv4(), operator: 'contains', value: '' });

const normalizeFilter = (filterValue) => {
  if (!filterValue) return null;

  return {
    logicOperator: filterValue.logicOperator ?? 'or',
    items: (filterValue.items ?? []).map((item) => ({
      field: item.field ?? 'component_item_code',
      operator: item.operator ?? 'contains',
      value: item.value ?? '',
      itemLogicOperator: item.itemLogicOperator ?? filterValue.logicOperator ?? 'or',
    })),
  };
};

const areFiltersEqual = (leftFilter, rightFilter) =>
  JSON.stringify(normalizeFilter(leftFilter)) === JSON.stringify(normalizeFilter(rightFilter));

const mapFilterToRows = (filterValue) => {
  if (!filterValue?.items?.length) return [defaultRow()];

  return filterValue.items.map((item) => ({
    id: uuidv4(),
    operator: item.operator ?? 'contains',
    value: item.value ?? '',
  }));
};

const MaterialPlanItemFilter = ({ value, onChange, disabled }) => {
  const [logicOperator, setLogicOperator] = useState('or');
  const [rows, setRows] = useState([defaultRow()]);
  const lastReceivedFilterRef = useRef(undefined);
  const lastEmittedFilterRef = useRef(null);

  const buildFilter = useCallback((currentRows, currentLogic) => {
    const valid = currentRows.filter((r) => r.value.trim());
    if (valid.length === 0) return null;
    return {
      logicOperator: currentLogic,
      items: valid.map((r) => ({
        field: 'component_item_code',
        operator: r.operator,
        value: r.value,
        itemLogicOperator: currentLogic,
      })),
    };
  }, []);

  const currentFilter = useMemo(() => buildFilter(rows, logicOperator), [rows, logicOperator, buildFilter]);

  useEffect(() => {
    if (areFiltersEqual(lastReceivedFilterRef.current, value)) return;

    lastReceivedFilterRef.current = value;

    if (areFiltersEqual(value, currentFilter)) {
      lastEmittedFilterRef.current = value;
      return;
    }

    lastEmittedFilterRef.current = value;

    setLogicOperator(value?.logicOperator ?? 'or');
    setRows(mapFilterToRows(value));
  }, [value, currentFilter]);

  useEffect(() => {
    if (areFiltersEqual(lastEmittedFilterRef.current, currentFilter)) return;

    lastEmittedFilterRef.current = currentFilter;
    onChange(currentFilter);
  }, [currentFilter, onChange]);

  const handleAddRow = () => setRows((prev) => [...prev, defaultRow()]);

  const handleRemoveRow = (id) =>
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length > 0 ? next : [defaultRow()];
    });

  const handleRowChange = (id, field, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 2,
        bgcolor: 'grey.50',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 2, alignItems: { xs: 'flex-start', sm: 'center' } }}
      >
        <FormLabelUI text="Cikkszám szűrő" />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: { xs: 0, sm: 'auto' } }}>
          <Typography variant="caption" color="text.secondary">
            Kapcsolat:
          </Typography>
          <ToggleButtonGroup
            value={logicOperator}
            exclusive
            size="small"
            onChange={(_, val) => {
              if (val) setLogicOperator(val);
            }}
            disabled={disabled}
          >
            <ToggleButton value="or" sx={{ px: 2, py: 0.3, fontSize: '0.72rem' }}>
              VAGY
            </ToggleButton>
            <ToggleButton value="and" sx={{ px: 2, py: 0.3, fontSize: '0.72rem' }}>
              ÉS
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Stack spacing={1}>
        {rows.map((row, index) => (
          <Stack key={row.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                width: 40,
                textAlign: 'center',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: 'text.disabled',
              }}
            >
              {index === 0 ? '' : logicOperator === 'or' ? 'vagy' : 'és'}
            </Typography>
            <FormControl size="small" variant="standard" sx={{ minWidth: 155 }}>
              <Select
                value={row.operator}
                onChange={(e) => handleRowChange(row.id, 'operator', e.target.value)}
                disabled={disabled}
              >
                {OPERATORS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              variant="standard"
              value={row.value}
              onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
              disabled={disabled}
              placeholder="értéket írjon be..."
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => handleRemoveRow(row.id)} disabled={disabled || rows.length === 1}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow} disabled={disabled} variant="text">
          Feltétel hozzáadása
        </Button>
      </Box>
    </Box>
  );
};

export default MaterialPlanItemFilter;
